import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Avatar,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  FilterAltOff,
  FilterList,
  Visibility,
  Search,
  History,
  People,
  Storefront,
  Category,
  Event,
  Email,
  LocationOn,
  TrendingUp,
  ShowChart,
  BarChart,
  Close,
  Campaign,
  Refresh,
  Hotel,
  Download,
} from '@mui/icons-material';
import { userActivityAPI, categoriesAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const formatDateLabel = (dateStr) => {
  if (!dateStr) return '';
  try {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const MODULE_OPTIONS = [
  { key: 'Contact', label: 'Contact Activity', icon: <Storefront />, color: '#328284' },
  { key: 'Event', label: 'Event Activity', icon: <Event />, color: '#7E22CE' },
  { key: 'Promotion', label: 'Promotion Activity', icon: <Campaign />, color: '#D97706' },
  { key: 'Accommodation', label: 'Accommodation Activity', icon: <Hotel />, color: '#2563EB' },
];

const UserActivity = () => {
  const { t } = useLanguage();

  // Selected Activity Module: 'Contact' | 'Event' | 'Promotion' | 'Accommodation'
  const [activeModule, setActiveModule] = useState('Contact');

  // Common Global Filter States for Year, Month, Week
  const currentYearStr = String(new Date().getFullYear());
  const currentMonthStr = String(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYearStr);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [selectedWeek, setSelectedWeek] = useState('all');

  // Helper to retrieve safe translation or fallback string if key is unparsed
  const getSafeTranslation = useCallback((key, fallback) => {
    const val = t(key);
    if (!val || val === key || val.includes('.')) {
      return fallback;
    }
    return val;
  }, [t]);

  // Available options
  const availableYearsOptions = useMemo(() => {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(currentYear, startYear);
    const years = [];
    for (let y = maxYear; y >= startYear; y--) {
      const curLabel = getSafeTranslation('userActivity.currentYear', 'Current Year');
      years.push({
        value: String(y),
        label: y === currentYear ? `${y} (${curLabel})` : String(y),
      });
    }
    return years;
  }, [getSafeTranslation]);

  const MONTHS_OPTIONS = useMemo(() => [
    { value: '1', label: getSafeTranslation('months.january', 'January') },
    { value: '2', label: getSafeTranslation('months.february', 'February') },
    { value: '3', label: getSafeTranslation('months.march', 'March') },
    { value: '4', label: getSafeTranslation('months.april', 'April') },
    { value: '5', label: getSafeTranslation('months.may', 'May') },
    { value: '6', label: getSafeTranslation('months.june', 'June') },
    { value: '7', label: getSafeTranslation('months.july', 'July') },
    { value: '8', label: getSafeTranslation('months.august', 'August') },
    { value: '9', label: getSafeTranslation('months.september', 'September') },
    { value: '10', label: getSafeTranslation('months.october', 'October') },
    { value: '11', label: getSafeTranslation('months.november', 'November') },
    { value: '12', label: getSafeTranslation('months.december', 'December') },
  ], [getSafeTranslation]);


  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  // API State
  const [activitiesList, setActivitiesList] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalEngagements: 0,
    uniqueUsers: 0,
    activeEntities: 0,
    activeCategories: 0,
  });
  const [trendData, setTrendData] = useState([]);
  const [topEntities, setTopEntities] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hoveredPointIndex, setHoveredPointIndex] = useState(null);

  // Filtering state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [categoriesList, setCategoriesList] = useState([]);

  // Fetch categories dropdown data for Contact module
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const catRes = await categoriesAPI.getCategories(1, 0, 100, { hasContact: true });

        let cats = [];
        if (catRes?.data?.payload && Array.isArray(catRes.data.payload)) {
          cats = catRes.data.payload;
        } else if (Array.isArray(catRes?.data)) {
          cats = catRes.data;
        }

        const contactCats = cats.filter((c) => c.hasContact === true || c.hasContact === 'true');
        setCategoriesList(contactCats.length > 0 ? contactCats : cats);
      } catch (err) {
        console.error('Error fetching categories for UserActivity dropdown:', err);
      }
    };
    fetchCategories();
  }, []);

  // Table Pagination state (0-indexed for MUI TablePagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Entity Details Dialog State
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEntityDetail, setSelectedEntityDetail] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Combined Analytics Fetcher (Executes 3 API calls: Summary, Trend, and Top Entities)
  const fetchAnalyticsData = useCallback(async (moduleName, yearVal, monthVal, weekVal) => {
    try {
      let numDays = 30;
      let periodParam = 'month';
      let groupBy = 'day';

      if (weekVal !== 'all') {
        numDays = 7;
        periodParam = 'week';
        groupBy = 'day';
      } else if (monthVal === 'today') {
        numDays = 1;
        periodParam = 'day';
        groupBy = 'day';
      } else if (monthVal !== 'all') {
        numDays = 30;
        periodParam = 'month';
        groupBy = 'day';
      } else if (yearVal !== 'all') {
        numDays = 365;
        periodParam = 'year';
        groupBy = 'month';
      } else {
        numDays = 365;
        periodParam = 'year';
        groupBy = 'month';
      }

      const filterOptions = weekVal === 'current_week' ? { period: 'week' } : { year: yearVal, month: monthVal, week: weekVal, period: periodParam };

      // Execute 3 API calls simultaneously for Summary, Trend, and Top Entities
      let [summaryRes, trendRes, topRes] = await Promise.allSettled([
        userActivityAPI.getSummary(moduleName, numDays, periodParam, weekVal === 'current_week' ? '' : groupBy, filterOptions),
        userActivityAPI.getTrend(moduleName, numDays, periodParam, groupBy, filterOptions),
        userActivityAPI.getTop(moduleName, 10, numDays, periodParam, groupBy, filterOptions),
      ]);

      if (moduleName === 'Accommodation') {
        const hasData = summaryRes.status === 'fulfilled' && summaryRes.value?.data?.success;
        if (!hasData) {
          const fallbackRes = await Promise.allSettled([
            userActivityAPI.getSummary('Accomodation', numDays, periodParam, weekVal === 'current_week' ? '' : groupBy, filterOptions),
            userActivityAPI.getTrend('Accomodation', numDays, periodParam, groupBy, filterOptions),
            userActivityAPI.getTop('Accomodation', 10, numDays, periodParam, groupBy, filterOptions),
          ]);
          [summaryRes, trendRes, topRes] = fallbackRes;
        }
      }

      // 1. Process Trend
      let newTrend = [];
      if (trendRes.status === 'fulfilled' && trendRes.value?.data?.success && Array.isArray(trendRes.value.data.payload)) {
        newTrend = trendRes.value.data.payload;
        setTrendData(newTrend);
      } else {
        setTrendData([]);
      }

      // 2. Process Top Entities
      if (topRes.status === 'fulfilled' && topRes.value?.data?.success && Array.isArray(topRes.value.data.payload)) {
        setTopEntities(topRes.value.data.payload);
      } else {
        setTopEntities([]);
      }

      const periodVisits = newTrend.reduce((sum, item) => sum + (item.visits || 0), 0);
      const periodUnique = newTrend.reduce((sum, item) => sum + (item.uniqueVisitors || 0), 0);

      // 3. Process Summary
      if (summaryRes.status === 'fulfilled' && summaryRes.value?.data?.success && summaryRes.value.data.payload) {
        const payload = summaryRes.value.data.payload;
        setSummaryData({
          totalEngagements: payload.totalEngagements || periodVisits,
          uniqueUsers: payload.uniqueUsers || periodUnique,
          activeEntities: payload.activeEntities || 0,
          activeCategories: payload.activeCategories || 0,
        });
      } else {
        setSummaryData({
          totalEngagements: periodVisits,
          uniqueUsers: periodUnique,
          activeEntities: 0,
          activeCategories: 0,
        });
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
    }
  }, []);

  // Main Module Load
  const fetchModuleData = useCallback(async (
    moduleName,
    pageIndex,
    pageSize,
    catId = selectedCategory,
    search = searchQuery,
    dateVal = selectedDate
  ) => {
    setIsLoading(true);
    try {
      const apiOptions = {};
      if (catId && catId !== 'all') {
        apiOptions.categoryId = catId;
        apiOptions.category = catId;
      }
      if (search && search.trim()) {
        apiOptions.search = search.trim();
      }
      if (dateVal && String(dateVal).trim()) {
        const cleanDate = String(dateVal).trim();
        apiOptions.date = cleanDate;
        apiOptions.selectedDate = cleanDate;
        apiOptions.selectDate = cleanDate;
      }

      let listRes = await userActivityAPI.getActivityList(moduleName, pageIndex + 1, pageSize, apiOptions);
      if (moduleName === 'Accommodation') {
        const hasList = listRes?.data?.success && Array.isArray(listRes?.data?.payload) && listRes.data.payload.length > 0;
        if (!hasList) {
          listRes = await userActivityAPI.getActivityList('Accomodation', pageIndex + 1, pageSize, apiOptions);
        }
      }

      if (listRes?.data?.success) {
        const payload = listRes.data.payload;
        setActivitiesList(Array.isArray(payload) ? payload : []);
        setTotalRecords(listRes.data.totalRecord || (Array.isArray(payload) ? payload.length : 0));
      } else {
        setActivitiesList([]);
        setTotalRecords(0);
      }

      await fetchAnalyticsData(moduleName, selectedYear, selectedMonth, selectedWeek);
    } catch (err) {
      console.error(`Error loading ${moduleName} user activity:`, err);
      setSnackbar({ open: true, message: `Failed to load ${moduleName} user activity data`, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchAnalyticsData, selectedYear, selectedMonth, selectedWeek, selectedCategory, searchQuery, selectedDate]);
  // Global Filter Handlers
  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    fetchAnalyticsData(activeModule, newYear, selectedMonth, selectedWeek);
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    fetchAnalyticsData(activeModule, selectedYear, newMonth, selectedWeek);
  };

  const handleWeekChange = (e) => {
    const newWeek = e.target.value;
    setSelectedWeek(newWeek);
    fetchAnalyticsData(activeModule, selectedYear, selectedMonth, newWeek);
  };

  // Export & Download XLS Report (Microsoft Excel Spreadsheet .xls format)
  const handleDownloadXLS = () => {
    try {
      const sanitize = (str) => {
        if (str === null || str === undefined) return '';
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');
      };

      const formattedDate = (d) => {
        if (!d) return 'N/A';
        return sanitize(formatDateTime(d));
      };

      const xlsParts = [];
      xlsParts.push('<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">');
      xlsParts.push('<head>');
      xlsParts.push('<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />');
      xlsParts.push('<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>');
      xlsParts.push(`<x:Name>${sanitize(activeModule)} Activity Report</x:Name>`);
      xlsParts.push('<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>');
      xlsParts.push('</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->');
      xlsParts.push('<style>');
      xlsParts.push('  body { font-family: Calibri, Arial, sans-serif; }');
      xlsParts.push('  .title-header { background-color: #0F3D3E; color: #FFFFFF; font-weight: bold; font-size: 16pt; padding: 12px; }');
      xlsParts.push('  .meta-info { font-size: 10pt; color: #555555; padding: 5px; }');
      xlsParts.push('  .section-header { background-color: #328284; color: #FFFFFF; font-weight: bold; font-size: 12pt; padding: 8px; }');
      xlsParts.push('  .th-header { background-color: #F3F4F6; color: #374151; font-weight: bold; border: 1px solid #D1D5DB; padding: 8px; text-align: left; }');
      xlsParts.push('  .td-cell { border: 1px solid #E5E7EB; padding: 8px; font-size: 10pt; vertical-align: middle; }');
      xlsParts.push('  .kpi-num { font-weight: bold; color: #0F3D3E; font-size: 12pt; }');
      xlsParts.push('</style>');
      xlsParts.push('</head>');
      xlsParts.push('<body>');

      // Title Block
      xlsParts.push('<table>');
      xlsParts.push(`  <tr><td colspan="9" class="title-header">${sanitize(activeModule)} Activity Analytics Report</td></tr>`);
      xlsParts.push(`  <tr><td colspan="9" class="meta-info">Generated Date: ${formattedDate(new Date().toISOString())} | Filter Period: Year: ${sanitize(selectedYear)}, Month: ${sanitize(selectedMonth)}, Week: ${sanitize(selectedWeek)}</td></tr>`);
      xlsParts.push('  <tr><td colspan="9"></td></tr>');

      // Summary KPIs Block
      xlsParts.push('  <tr><td colspan="9" class="section-header">Summary KPIs</td></tr>');
      xlsParts.push('  <tr>');
      xlsParts.push('    <td colspan="2" class="th-header">Total User Visits</td>');
      xlsParts.push('    <td colspan="2" class="th-header">Unique Users</td>');
      xlsParts.push('    <td colspan="2" class="th-header">Active Items</td>');
      xlsParts.push('    <td colspan="3" class="th-header">Active Categories</td>');
      xlsParts.push('  </tr>');
      xlsParts.push('  <tr>');
      xlsParts.push(`    <td colspan="2" class="td-cell kpi-num">${summaryData.totalEngagements || 0}</td>`);
      xlsParts.push(`    <td colspan="2" class="td-cell kpi-num">${summaryData.uniqueUsers || 0}</td>`);
      xlsParts.push(`    <td colspan="2" class="td-cell kpi-num">${summaryData.activeEntities || 0}</td>`);
      xlsParts.push(`    <td colspan="3" class="td-cell kpi-num">${summaryData.activeCategories || 0}</td>`);
      xlsParts.push('  </tr>');
      xlsParts.push('  <tr><td colspan="9"></td></tr>');

      // Activity Trend Block
      xlsParts.push('  <tr><td colspan="9" class="section-header">Activity Visit Trend</td></tr>');
      xlsParts.push('  <tr>');
      xlsParts.push(`    <td colspan="3" class="th-header">${(selectedMonth === 'all' && selectedWeek === 'all') ? 'Month Name' : 'Date / Day'}</td>`);
      xlsParts.push('    <td colspan="3" class="th-header">Total Visits</td>');
      xlsParts.push('    <td colspan="3" class="th-header">Unique Visitors</td>');
      xlsParts.push('  </tr>');
      (formattedTrend || []).forEach((t) => {
        xlsParts.push('  <tr>');
        xlsParts.push(`    <td colspan="3" class="td-cell">${sanitize(t.fullLabel || t.dayLabel)}</td>`);
        xlsParts.push(`    <td colspan="3" class="td-cell">${t.visits || 0}</td>`);
        xlsParts.push(`    <td colspan="3" class="td-cell">${t.uniqueVisitors || 0}</td>`);
        xlsParts.push('  </tr>');
      });
      xlsParts.push('  <tr><td colspan="9"></td></tr>');

      // Top Engaged Items
      xlsParts.push(`  <tr><td colspan="9" class="section-header">Top 10 ${sanitize(activeModule)} Items</td></tr>`);
      xlsParts.push('  <tr>');
      xlsParts.push('    <td class="th-header">Rank</td>');
      xlsParts.push('    <td colspan="5" class="th-header">Item Name</td>');
      xlsParts.push('    <td colspan="3" class="th-header">Total Visits</td>');
      xlsParts.push('  </tr>');
      (topEntities || []).forEach((item, idx) => {
        xlsParts.push('  <tr>');
        xlsParts.push(`    <td class="td-cell">${idx + 1}</td>`);
        xlsParts.push(`    <td colspan="5" class="td-cell"><strong>${sanitize(item.entityName || `Item #${item.entityId}`)}</strong></td>`);
        xlsParts.push(`    <td colspan="3" class="td-cell">${item.totalVisits || 0}</td>`);
        xlsParts.push('  </tr>');
      });
      xlsParts.push('  <tr><td colspan="9"></td></tr>');

      // Detailed Activity Records Table
      xlsParts.push(`  <tr><td colspan="9" class="section-header">Detailed ${sanitize(activeModule)} Activity Log</td></tr>`);
      xlsParts.push('  <tr>');
      xlsParts.push('    <td class="th-header">#</td>');
      xlsParts.push(`    <td class="th-header">${sanitize(activeModule)} Name</td>`);
      xlsParts.push('    <td class="th-header">Email</td>');
      xlsParts.push('    <td class="th-header">Category</td>');
      xlsParts.push('    <td class="th-header">Type</td>');
      xlsParts.push('    <td class="th-header">City</td>');
      xlsParts.push('    <td class="th-header">Total Visits</td>');
      xlsParts.push('    <td class="th-header">Unique Visitors</td>');
      xlsParts.push('    <td class="th-header">Last Activity Date</td>');
      xlsParts.push('  </tr>');

      (filteredActivities || []).forEach((row, idx) => {
        xlsParts.push('  <tr>');
        xlsParts.push(`    <td class="td-cell">${idx + 1}</td>`);
        xlsParts.push(`    <td class="td-cell"><strong>${sanitize(row.entityName || `Item #${row.entityId}`)}</strong></td>`);
        xlsParts.push(`    <td class="td-cell">${sanitize(row.entityEmail || 'N/A')}</td>`);
        xlsParts.push(`    <td class="td-cell">${sanitize(row.categoryName || 'N/A')}</td>`);
        xlsParts.push(`    <td class="td-cell">${sanitize(row.typeName || 'N/A')}</td>`);
        xlsParts.push(`    <td class="td-cell">${sanitize(row.cityName || 'Germany')}</td>`);
        xlsParts.push(`    <td class="td-cell">${row.totalVisits || 0}</td>`);
        xlsParts.push(`    <td class="td-cell">${row.uniqueVisitors || 0}</td>`);
        xlsParts.push(`    <td class="td-cell">${formattedDate(row.lastActivityAtUtc)}</td>`);
        xlsParts.push('  </tr>');
      });

      xlsParts.push('</table>');
      xlsParts.push('</body>');
      xlsParts.push('</html>');

      const xlsContent = xlsParts.join('\n');
      const blob = new Blob([xlsContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `${activeModule}_Activity_Report_${selectedYear}_${selectedMonth}_${dateStr}.xls`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `XLS report downloaded successfully for ${activeModule} (${selectedYear}/${selectedMonth})`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Error generating XLS report:', err);
      setSnackbar({ open: true, message: 'Failed to download XLS report', severity: 'error' });
    }
  };

  useEffect(() => {
    fetchModuleData(activeModule, page, rowsPerPage);
  }, [fetchModuleData, activeModule, page, rowsPerPage]);

  // Fetch Entity Details for View Icon Click
  const fetchEntityDetail = useCallback(async (moduleName, entityId, pIndex = 0, pSize = 10) => {
    setIsDetailLoading(true);
    try {
      let res = await userActivityAPI.getEntityDetail(moduleName, entityId, pIndex + 1, pSize);
      if ((!res?.data?.success || !res?.data?.payload) && moduleName === 'Accommodation') {
        res = await userActivityAPI.getEntityDetail('Accomodation', entityId, pIndex + 1, pSize);
      }
      if (res?.data?.success && res.data.payload) {
        setSelectedEntityDetail(res.data.payload);
      } else {
        setSelectedEntityDetail(null);
        setSnackbar({ open: true, message: 'Failed to load visitor activity details', severity: 'error' });
      }
    } catch (err) {
      console.error(`Error loading entity detail for ${moduleName} ${entityId}:`, err);
      setSnackbar({ open: true, message: 'Error loading visitor activity details', severity: 'error' });
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  const handleViewClick = (row) => {
    setSelectedEntityDetail(null);
    setDetailsDialogOpen(true);
    fetchEntityDetail(activeModule, row.entityId, 0, 20);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedEntityDetail(null);
  };

  // Categories list for filter dropdown containing only Contact categories with ID and title
  const categoryOptions = useMemo(() => {
    const list = [];
    const seen = new Set();
    categoriesList.forEach((cat) => {
      const id = cat.categoryId || cat.id || cat.CategoryId;
      const title = cat.title_EN || cat.title_En || cat.title || cat.name || cat.categoryName || cat.title_DE || `Category #${id}`;
      if (id != null && !seen.has(String(id))) {
        seen.add(String(id));
        list.push({ id: String(id), name: title.trim() });
      }
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [categoriesList]);

  // Filtered List Computation (Client-Side filter fallback on loaded page items)
  const filteredActivities = useMemo(() => {
    return activitiesList.filter((item) => {
      // Search by Entity Name / Email / Category
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const nameMatch = (item.entityName || '').toLowerCase().includes(query);
        const emailMatch = (item.entityEmail || '').toLowerCase().includes(query);
        const categoryMatch = (item.categoryName || '').toLowerCase().includes(query);
        if (!nameMatch && !emailMatch && !categoryMatch) return false;
      }

      // Category Dropdown Filter
      if (selectedCategory !== 'all') {
        const itemCatId = String(item.categoryId || item.id || item.CategoryId || '');
        const itemCatName = (item.categoryName || '').toLowerCase().trim();
        const selectedObj = categoryOptions.find((c) => c.id === String(selectedCategory));
        const targetCatName = selectedObj ? selectedObj.name.toLowerCase() : '';

        if (itemCatId && itemCatId === String(selectedCategory)) {
          // match by ID
        } else if (targetCatName && itemCatName === targetCatName) {
          // match by Name
        } else if (!itemCatId && !targetCatName) {
          // no info available, keep
        } else {
          return false;
        }
      }

      // Selected Date Filter
      if (selectedDate) {
        const targetDate = new Date(selectedDate);
        const itemDate = new Date(item.lastActivityAtUtc);
        if (isNaN(itemDate.getTime())) return false;
        if (
          itemDate.getFullYear() !== targetDate.getFullYear() ||
          itemDate.getMonth() !== targetDate.getMonth() ||
          itemDate.getDate() !== targetDate.getDate()
        ) {
          return false;
        }
      }

      return true;
    });
  }, [activitiesList, searchQuery, selectedCategory, selectedDate, categoryOptions]);

  const handleDateChange = (e) => {
    const val = e.target.value;
    setSelectedDate(val);
    setPage(0);
    fetchModuleData(activeModule, 0, rowsPerPage, selectedCategory, searchQuery, val);
  };

  const handleCategorySelect = (e) => {
    const val = e.target.value;
    setSelectedCategory(val);
    setPage(0);
    fetchModuleData(activeModule, 0, rowsPerPage, val, searchQuery, selectedDate);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedDate('');
    setPage(0);
    fetchModuleData(activeModule, 0, rowsPerPage, 'all', '', '');
  };

  // Format Trend Data for Week, Month, and Year period views
  const formattedTrend = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const effectiveYear = selectedYear !== 'all' ? parseInt(selectedYear, 10) : now.getFullYear();
    const effectiveMonth = (selectedMonth !== 'all' && selectedMonth !== 'today') ? parseInt(selectedMonth, 10) : (now.getMonth() + 1);

    // 1. WEEK FILTER IS ACTIVE (current_week or Week 1..5)
    if (selectedWeek !== 'all') {
      const weekDaysMap = [];

      if (selectedWeek === 'current_week') {
        const curr = new Date();
        const dayOfWeek = curr.getDay(); // 0 is Sun, 1 is Mon...
        const distanceToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(curr);
        monday.setDate(curr.getDate() + distanceToMon);

        for (let i = 0; i < 7; i++) {
          const d = new Date(monday);
          d.setDate(monday.getDate() + i);
          const dateKey = d.toISOString().split('T')[0];
          const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' });
          const fullLabel = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          weekDaysMap.push({
            dateKey,
            dayLabel,
            fullLabel,
            visits: 0,
            uniqueVisitors: 0,
            dayNum: d.getDate(),
            monthNum: d.getMonth(),
          });
        }
      } else if (['1', '2', '3', '4', '5'].includes(selectedWeek)) {
        const weekNum = parseInt(selectedWeek, 10);
        const startDay = (weekNum - 1) * 7 + 1;
        const daysInMonth = new Date(effectiveYear, effectiveMonth, 0).getDate();
        const endDay = Math.min(weekNum * 7, daysInMonth);

        for (let day = startDay; day <= endDay; day++) {
          const d = new Date(effectiveYear, effectiveMonth - 1, day);
          const dateKey = d.toISOString().split('T')[0];
          const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
          const fullLabel = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          weekDaysMap.push({
            dateKey,
            dayLabel,
            fullLabel,
            visits: 0,
            uniqueVisitors: 0,
            dayNum: d.getDate(),
            monthNum: d.getMonth(),
          });
        }
      }

      if (Array.isArray(trendData) && trendData.length > 0) {
        trendData.forEach((item, idx) => {
          const dStr = item.dayUtc || item.date || item.createdAt;
          let matched = false;

          if (dStr) {
            const d = new Date(dStr);
            if (!isNaN(d.getTime())) {
              const itemKey = d.toISOString().split('T')[0];
              const found = weekDaysMap.find(
                (w) => w.dateKey === itemKey || (w.dayNum === d.getDate() && w.monthNum === d.getMonth())
              );
              if (found) {
                found.visits += item.visits || item.totalVisits || item.count || 0;
                found.uniqueVisitors += item.uniqueVisitors || item.uniqueUsers || 0;
                matched = true;
              }
            }
          }

          if (!matched && (item.dayLabel || item.dayName || item.weekday)) {
            const name = String(item.dayLabel || item.dayName || item.weekday).toLowerCase();
            const foundByName = weekDaysMap.find((w) => name.startsWith(w.dayLabel.toLowerCase()));
            if (foundByName) {
              foundByName.visits += item.visits || item.totalVisits || item.count || 0;
              foundByName.uniqueVisitors += item.uniqueVisitors || item.uniqueUsers || 0;
              matched = true;
            }
          }

          if (!matched && idx < weekDaysMap.length) {
            weekDaysMap[idx].visits += item.visits || item.totalVisits || item.count || 0;
            weekDaysMap[idx].uniqueVisitors += item.uniqueVisitors || item.uniqueUsers || 0;
          }
        });
      }

      return weekDaysMap.map(({ dayLabel, fullLabel, visits, uniqueVisitors }) => ({
        dayLabel,
        fullLabel,
        visits,
        uniqueVisitors,
      }));
    }

    // 2. MONTH FILTER IS ACTIVE (Specific Month 1..12 or 'today') AND selectedWeek === 'all'
    if (selectedMonth !== 'all') {
      if (selectedMonth === 'today') {
        const todayStr = now.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        const totalV = (Array.isArray(trendData) ? trendData : []).reduce((s, i) => s + (i.visits || i.totalVisits || i.count || 0), 0);
        const totalU = (Array.isArray(trendData) ? trendData : []).reduce((s, i) => s + (i.uniqueVisitors || i.uniqueUsers || 0), 0);
        return [
          {
            dayLabel: 'Today',
            fullLabel: `Today (${todayStr})`,
            visits: totalV,
            uniqueVisitors: totalU,
          },
        ];
      }

      const targetMonthNum = parseInt(selectedMonth, 10);
      const daysInMonth = new Date(effectiveYear, targetMonthNum, 0).getDate();
      const monthNameShort = monthNames[targetMonthNum - 1] || 'Month';

      const monthDaysMap = [];
      for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
        monthDaysMap.push({
          dayLabel: `${dayNum}`,
          fullLabel: `${monthNameShort} ${dayNum}, ${effectiveYear}`,
          visits: 0,
          uniqueVisitors: 0,
          dayNum,
        });
      }

      if (Array.isArray(trendData) && trendData.length > 0) {
        trendData.forEach((item, idx) => {
          let dayIdx = -1;
          const dStr = item.dayUtc || item.date || item.createdAt;
          if (dStr) {
            const d = new Date(dStr);
            if (!isNaN(d.getTime())) {
              dayIdx = d.getDate() - 1;
            }
          }
          if (dayIdx === -1 && item.day !== undefined && typeof item.day === 'number') {
            dayIdx = item.day - 1;
          }
          if (dayIdx === -1 && idx < monthDaysMap.length) {
            dayIdx = idx;
          }

          if (dayIdx >= 0 && dayIdx < monthDaysMap.length) {
            monthDaysMap[dayIdx].visits += item.visits || item.totalVisits || item.count || 0;
            monthDaysMap[dayIdx].uniqueVisitors += item.uniqueVisitors || item.uniqueUsers || 0;
          }
        });
      }

      return monthDaysMap;
    }

    // 3. YEAR FILTER (or Default 12 Months: Jan - Dec)
    const monthlyMap = monthNames.map((m) => ({
      dayLabel: m,
      fullLabel: `${m} ${effectiveYear}`,
      visits: 0,
      uniqueVisitors: 0,
    }));

    if (Array.isArray(trendData) && trendData.length > 0) {
      trendData.forEach((item, idx) => {
        let mIdx = -1;

        if (item.month !== undefined && item.month !== null) {
          if (typeof item.month === 'number') {
            if (item.month >= 1 && item.month <= 12) mIdx = item.month - 1;
            else if (item.month >= 0 && item.month <= 11) mIdx = item.month;
          } else if (typeof item.month === 'string') {
            const parsed = parseInt(item.month, 10);
            if (!isNaN(parsed) && parsed >= 1 && parsed <= 12) mIdx = parsed - 1;
            else {
              const strLower = item.month.toLowerCase().trim();
              mIdx = monthNames.findIndex((m) => strLower.startsWith(m.toLowerCase()));
            }
          }
        }

        if (mIdx === -1) {
          const dStr = item.dayUtc || item.date || item.createdAt;
          if (dStr) {
            const d = new Date(dStr);
            if (!isNaN(d.getTime())) mIdx = d.getMonth();
          }
        }

        if (mIdx === -1 && trendData.length <= 12) {
          mIdx = idx % 12;
        }

        const vVal = item.visits || item.totalVisits || item.count || 0;
        const uVal = item.uniqueVisitors || item.uniqueUsers || 0;

        if (mIdx >= 0 && mIdx < 12) {
          monthlyMap[mIdx].visits += vVal;
          monthlyMap[mIdx].uniqueVisitors += uVal;
        }
      });
    }

    return monthlyMap;
  }, [trendData, selectedYear, selectedMonth, selectedWeek]);

  // Compute SVG coordinates and dynamic canvas width for BOTH Visits and Unique Users dual lines
  const { trendPoints, visitsLinePath, visitsAreaPath, uniqueLinePath, uniqueAreaPath, svgWidth, maxVal } = useMemo(() => {
    if (!formattedTrend || formattedTrend.length === 0) {
      return { trendPoints: [], visitsLinePath: '', visitsAreaPath: '', uniqueLinePath: '', uniqueAreaPath: '', svgWidth: 500, maxVal: 100 };
    }

    const computedWidth = 500;
    const maxValFound = Math.max(
      ...formattedTrend.map((d) => Math.max(d.visits || 0, d.uniqueVisitors || 0)),
      10
    );
    const maxValScaled = Math.ceil(maxValFound / 10) * 10;

    const minX = 22; // Left edge space for Y-axis numbers
    const maxX = computedWidth - 5; // Right edge flush
    const minY = 30;
    const maxY = 240;

    const points = formattedTrend.map((item, idx) => {
      const x = formattedTrend.length === 1 ? computedWidth / 2 : minX + (idx * (maxX - minX)) / (formattedTrend.length - 1);

      const vVal = item.visits || 0;
      const vRatio = vVal / maxValScaled;
      const yVisits = maxY - vRatio * (maxY - minY);

      const uVal = item.uniqueVisitors || 0;
      const uRatio = uVal / maxValScaled;
      const yUnique = maxY - uRatio * (maxY - minY);

      return {
        ...item,
        x,
        yVisits,
        yUnique,
        val: vVal,
        uniqueVisitors: uVal,
        index: idx,
      };
    });

    // Compute Bezier smooth curve for Visits
    let vPathStr = '';
    points.forEach((pt, idx) => {
      if (idx === 0) {
        vPathStr += `M ${pt.x} ${pt.yVisits}`;
      } else {
        const prev = points[idx - 1];
        const cx1 = prev.x + (pt.x - prev.x) / 2;
        const cy1 = prev.yVisits;
        const cx2 = prev.x + (pt.x - prev.x) / 2;
        const cy2 = pt.yVisits;
        vPathStr += ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${pt.x} ${pt.yVisits}`;
      }
    });

    const vAreaStr = points.length > 0 ? `${vPathStr} L ${points[points.length - 1].x} ${maxY} L ${points[0].x} ${maxY} Z` : '';

    // Compute Bezier smooth curve for Unique Users
    let uPathStr = '';
    points.forEach((pt, idx) => {
      if (idx === 0) {
        uPathStr += `M ${pt.x} ${pt.yUnique}`;
      } else {
        const prev = points[idx - 1];
        const cx1 = prev.x + (pt.x - prev.x) / 2;
        const cy1 = prev.yUnique;
        const cx2 = prev.x + (pt.x - prev.x) / 2;
        const cy2 = pt.yUnique;
        uPathStr += ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${pt.x} ${pt.yUnique}`;
      }
    });

    const uAreaStr = points.length > 0 ? `${uPathStr} L ${points[points.length - 1].x} ${maxY} L ${points[0].x} ${maxY} Z` : '';

    return {
      trendPoints: points,
      visitsLinePath: vPathStr,
      visitsAreaPath: vAreaStr,
      uniqueLinePath: uPathStr,
      uniqueAreaPath: uAreaStr,
      svgWidth: computedWidth,
      maxVal: maxValScaled,
    };
  }, [formattedTrend]);

  // Get active module color & label
  const currentModuleConfig = useMemo(() => {
    return MODULE_OPTIONS.find((m) => m.key === activeModule) || MODULE_OPTIONS[0];
  }, [activeModule]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Screen Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <History sx={{ fontSize: 36, color: '#328284' }} />
            {t('nav.userActivity') || 'User Activity Analytics'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('userActivity.subtitle') || 'Track visits and activity trends across modules.'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          {/* Activity Module Dropdown Select (Left side of Refresh Data) */}
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel id="activity-module-select-label">{t('userActivity.selectModule') || 'Activity Module'}</InputLabel>
            <Select
              labelId="activity-module-select-label"
              value={activeModule}
              label={t('userActivity.selectModule') || 'Activity Module'}
              onChange={(e) => {
                const newMod = e.target.value;
                if (newMod && newMod !== activeModule) {
                  setActiveModule(newMod);
                  setPage(0);
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedDate('');
                }
              }}
              sx={{ bgcolor: '#FFFFFF', borderRadius: '8px' }}
            >
              {MODULE_OPTIONS.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: opt.color, display: 'flex' }}>{opt.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {opt.key === 'Contact' ? (t('nav.contacts') || opt.label) : opt.key === 'Event' ? (t('nav.events') || opt.label) : opt.key === 'Promotion' ? (t('nav.promotions') || opt.label) : (t('nav.accommodation') || opt.label)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Refresh Data Button */}
          <Button
            variant="outlined"
            onClick={() => fetchModuleData(activeModule, page, rowsPerPage)}
            disabled={isLoading}
            startIcon={<Refresh />}
            sx={{
              borderColor: '#328284',
              color: '#328284',
              height: 40,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(50, 130, 132, 0.08)', borderColor: '#0F3D3E' },
            }}
          >
            {t('userActivity.refreshData') || 'Refresh Data'}
          </Button>

          {/* Download XLS Report Button */}
          <Button
            variant="contained"
            onClick={handleDownloadXLS}
            startIcon={<Download />}
            sx={{
              bgcolor: '#328284',
              color: '#FFFFFF',
              height: 40,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 2px 6px rgba(50, 130, 132, 0.25)',
              '&:hover': { bgcolor: '#0F3D3E' },
            }}
          >
            {t('userActivity.downloadXls') || 'Download XLS Report'}
          </Button>
        </Stack>
      </Box>

      {/* Common Analytics Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '12px',
          bgcolor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#E6F4F1', color: '#328284', width: 36, height: 36 }}>
            <FilterList fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
              {getSafeTranslation('userActivity.filterPeriod', 'Analytics Filter Period')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              {getSafeTranslation('userActivity.filterSubtitle', 'Select year, month, or week to filter overall analytics data across all charts')}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {/* Year Select Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="common-year-select-label" sx={{ fontSize: '0.85rem' }}>
              {getSafeTranslation('userActivity.selectYear', 'Year')}
            </InputLabel>
            <Select
              labelId="common-year-select-label"
              value={selectedYear}
              label={getSafeTranslation('userActivity.selectYear', 'Year')}
              onChange={handleYearChange}
              sx={{
                bgcolor: '#F9FAFB',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: 600,
                height: 38,
              }}
            >
              {availableYearsOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Month Select Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="common-month-select-label" sx={{ fontSize: '0.85rem' }}>
              {getSafeTranslation('userActivity.selectMonth', 'Month')}
            </InputLabel>
            <Select
              labelId="common-month-select-label"
              value={selectedMonth}
              label={getSafeTranslation('userActivity.selectMonth', 'Month')}
              onChange={handleMonthChange}
              sx={{
                bgcolor: '#F9FAFB',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: 600,
                height: 38,
              }}
            >
              {MONTHS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Current Week Filter Button */}
          <Button
            variant={selectedWeek === 'current_week' ? 'contained' : 'outlined'}
            onClick={() => {
              const nextWeek = selectedWeek === 'current_week' ? 'all' : 'current_week';
              setSelectedWeek(nextWeek);
              fetchAnalyticsData(activeModule, selectedYear, selectedMonth, nextWeek);
            }}
            startIcon={<Event />}
            sx={{
              height: 38,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              bgcolor: selectedWeek === 'current_week' ? '#328284' : '#FFFFFF',
              color: selectedWeek === 'current_week' ? '#FFFFFF' : '#374151',
              borderColor: selectedWeek === 'current_week' ? '#328284' : '#D1D5DB',
              boxShadow: selectedWeek === 'current_week' ? '0 2px 6px rgba(50, 130, 132, 0.25)' : 'none',
              '&:hover': {
                bgcolor: selectedWeek === 'current_week' ? '#0F3D3E' : '#F9FAFB',
                borderColor: selectedWeek === 'current_week' ? '#0F3D3E' : '#9CA3AF',
              },
            }}
          >
            {getSafeTranslation('userActivity.currentWeek', 'Current Week')}
          </Button>

          {/* Reset Filters button if non-default filter selected */}
          {(selectedYear !== currentYearStr || selectedMonth !== currentMonthStr || selectedWeek !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const curYr = String(new Date().getFullYear());
                const curMo = String(new Date().getMonth() + 1);
                setSelectedYear(curYr);
                setSelectedMonth(curMo);
                setSelectedWeek('all');
                fetchAnalyticsData(activeModule, curYr, curMo, 'all');
              }}
              startIcon={<FilterAltOff sx={{ fontSize: '0.95rem !important' }} />}
              sx={{
                height: 32,
                px: 1.2,
                minWidth: 'auto',
                borderRadius: '6px',
                borderColor: '#FCA5A5',
                color: '#EF4444',
                bgcolor: '#FEF2F2',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: '#FEE2E2',
                  borderColor: '#EF4444',
                  color: '#DC2626',
                },
              }}
            >
              {t('common.clearFilters') || 'Clear Filters'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Summary KPI Metric Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderLeft: '5px solid #328284' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700, textTransform: 'uppercase' }}>
                  {t('userActivity.kpiTotalVisits') || 'Total User Visits'}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F3D3E', mt: 0.5 }}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: '#328284' }} /> : summaryData.totalEngagements}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#E6F4F1', color: '#328284', width: 48, height: 48 }}>
                <TrendingUp />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderLeft: '5px solid #0284C7' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700, textTransform: 'uppercase' }}>
                  {t('userActivity.kpiUniqueUsers') || 'Unique Users'}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F3D3E', mt: 0.5 }}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: '#0284C7' }} /> : summaryData.uniqueUsers}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#E0F2FE', color: '#0284C7', width: 48, height: 48 }}>
                <People />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderLeft: `5px solid ${currentModuleConfig.color}` }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700, textTransform: 'uppercase' }}>
                  {t('userActivity.kpiActiveEntities') || 'Active'} {activeModule === 'Contact' ? (t('nav.contacts') || 'Contacts') : activeModule === 'Event' ? (t('nav.events') || 'Events') : activeModule === 'Promotion' ? (t('nav.promotions') || 'Promotions') : (t('nav.accommodation') || 'Accommodations')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F3D3E', mt: 0.5 }}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: currentModuleConfig.color }} /> : summaryData.activeEntities}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: `${currentModuleConfig.color}15`, color: currentModuleConfig.color, width: 48, height: 48 }}>
                {currentModuleConfig.icon}
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderLeft: '5px solid #16A34A' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700, textTransform: 'uppercase' }}>
                  {t('userActivity.kpiActiveCategories') || 'Active Categories'}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F3D3E', mt: 0.5 }}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: '#16A34A' }} /> : summaryData.activeCategories}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#DCFCE7', color: '#16A34A', width: 48, height: 48 }}>
                <Category />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Activity Analytics Graphs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Graph 1: User Engagement Trend Line/Area Chart */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ pt: 3, px: 0, pb: 0, borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 3, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChart sx={{ color: currentModuleConfig.color }} />
                  {activeModule} {t('userActivity.trendChartTitle') || 'Activity Trend'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {t('userActivity.trendChartSubtitle') || 'Visit volume over selected period'}
                </Typography>
              </Box>
            </Box>

            {/* SVG Dual-Line Graph Container: Fits 100% full view inside card for both Month and Year periods */}
            <Box
              sx={{
                width: '100%',
                overflowX: 'hidden',
                p: 0,
                m: 0,
                position: 'relative',
              }}
            >
              {trendPoints.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                    {t('userActivity.trendNoData') || `No trend data available for ${activeModule} activity.`}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <svg
                    viewBox={`0 0 ${svgWidth} 280`}
                    width="100%"
                    height="280"
                    onMouseMove={(e) => {
                      if (!trendPoints || trendPoints.length === 0) return;
                      const svgRect = e.currentTarget.getBoundingClientRect();
                      const mouseX = ((e.clientX - svgRect.left) / svgRect.width) * svgWidth;
                      let minDiff = Infinity;
                      let closestIdx = 0;
                      trendPoints.forEach((pt, idx) => {
                        const diff = Math.abs(pt.x - mouseX);
                        if (diff < minDiff) {
                          minDiff = diff;
                          closestIdx = idx;
                        }
                      });
                      setHoveredPointIndex(closestIdx);
                    }}
                    onMouseLeave={() => setHoveredPointIndex(null)}
                    style={{ overflow: 'visible', display: 'block', cursor: 'crosshair' }}
                  >
                    <defs>
                      <linearGradient id="visitsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity="0.0" />
                      </linearGradient>
                      <linearGradient id="uniqueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0284C7" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#0284C7" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>

                    {/* Left Y-Axis Scale Values */}
                    <text x="18" y="34" textAnchor="end" fontSize="10" fill="#9CA3AF" fontWeight="500">
                      {maxVal}
                    </text>
                    <text x="18" y="104" textAnchor="end" fontSize="10" fill="#9CA3AF" fontWeight="500">
                      {Math.round(maxVal * 0.66)}
                    </text>
                    <text x="18" y="174" textAnchor="end" fontSize="10" fill="#9CA3AF" fontWeight="500">
                      {Math.round(maxVal * 0.33)}
                    </text>
                    <text x="18" y="244" textAnchor="end" fontSize="10" fill="#9CA3AF" fontWeight="500">
                      0
                    </text>

                    {/* Horizontal Grid lines */}
                    <line x1="0" y1="30" x2={svgWidth} y2="30" stroke="#F1F5F9" strokeWidth="1" />
                    <line x1="0" y1="100" x2={svgWidth} y2="100" stroke="#F1F5F9" strokeWidth="1" />
                    <line x1="0" y1="170" x2={svgWidth} y2="170" stroke="#F1F5F9" strokeWidth="1" />
                    <line x1="0" y1="240" x2={svgWidth} y2="240" stroke="#E2E8F0" strokeWidth="1.5" />

                    {/* Area Fills */}
                    {uniqueAreaPath && <path d={uniqueAreaPath} fill="url(#uniqueGradient)" />}
                    {visitsAreaPath && <path d={visitsAreaPath} fill="url(#visitsGradient)" />}

                    {/* Line 1: Unique Users */}
                    {uniqueLinePath && (
                      <path
                        d={uniqueLinePath}
                        fill="none"
                        stroke="#0284C7"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}

                    {/* Line 2: Visits */}
                    {visitsLinePath && (
                      <path
                        d={visitsLinePath}
                        fill="none"
                        stroke="#2563EB"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}

                    {/* Default Data Points & Labels */}
                    {trendPoints.map((pt, idx) => {
                      const isHovered = hoveredPointIndex === idx;
                      const step = (selectedWeek !== 'all' || selectedMonth === 'today' || selectedMonth === 'all') ? 1 : Math.ceil(trendPoints.length / 8);
                      const showText = (selectedWeek !== 'all' || selectedMonth === 'today' || selectedMonth === 'all') || idx % step === 0 || idx === trendPoints.length - 1;

                      return (
                        <g key={idx} onMouseEnter={() => setHoveredPointIndex(idx)}>
                          {/* Point on Visits Line */}
                          {!isHovered && (
                            <circle cx={pt.x} cy={pt.yVisits} r="3.5" fill="#2563EB" stroke="#FFFFFF" strokeWidth="1.5" />
                          )}
                          {/* Point on Unique Users Line */}
                          {!isHovered && (
                            <circle cx={pt.x} cy={pt.yUnique} r="3" fill="#0284C7" stroke="#FFFFFF" strokeWidth="1.5" />
                          )}

                          {/* X-Axis Label */}
                          {!isHovered && showText && (
                            <text x={pt.x} y="261" textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#64748B">
                              {pt.dayLabel}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Active Hover Guidance Line, Dual Glowing Dots, Dark Pill Badge & Dark Floating Card */}
                    {hoveredPointIndex !== null && trendPoints[hoveredPointIndex] && (() => {
                      const activePt = trendPoints[hoveredPointIndex];
                      const cardWidth = 150;
                      const cardHeight = 74;
                      const cardX = activePt.x + cardWidth + 15 > svgWidth ? activePt.x - cardWidth - 12 : activePt.x + 12;
                      const cardY = Math.max(10, Math.min(130, Math.min(activePt.yVisits, activePt.yUnique) - 35));

                      return (
                        <g style={{ pointerEvents: 'none' }}>
                          {/* 1. Vertical Dashed Guideline */}
                          <line
                            x1={activePt.x}
                            y1={20}
                            x2={activePt.x}
                            y2={240}
                            stroke="#CBD5E1"
                            strokeWidth="1.5"
                            strokeDasharray="3 3"
                          />

                          {/* 2. Highlight Circle 1 (Visits) */}
                          <circle
                            cx={activePt.x}
                            cy={activePt.yVisits}
                            r="6.5"
                            fill="#2563EB"
                            stroke="#FFFFFF"
                            strokeWidth="2.5"
                          />

                          {/* 3. Highlight Circle 2 (Unique Users) */}
                          <circle
                            cx={activePt.x}
                            cy={activePt.yUnique}
                            r="6"
                            fill="#0284C7"
                            stroke="#FFFFFF"
                            strokeWidth="2.5"
                          />

                          {/* 4. Bottom X-Axis Active Dark Pill Badge with Pointer Triangle */}
                          <g>
                            <polygon
                              points={`${activePt.x - 5},240 ${activePt.x + 5},240 ${activePt.x},235`}
                              fill="#1E293B"
                            />
                            <rect
                              x={activePt.x - (activePt.dayLabel.length > 3 ? 24 : 19)}
                              y="240"
                              width={activePt.dayLabel.length > 3 ? 48 : 38}
                              height="22"
                              rx="4"
                              fill="#1E293B"
                            />
                            <text
                              x={activePt.x}
                              y="255"
                              textAnchor="middle"
                              fontSize="11"
                              fontWeight="700"
                              fill="#FFFFFF"
                            >
                              {activePt.dayLabel}
                            </text>
                          </g>

                          {/* 5. Dark Floating Tooltip Card */}
                          <g transform={`translate(${cardX}, ${cardY})`}>
                            <rect
                              width={cardWidth}
                              height={cardHeight}
                              rx="6"
                              fill="#1E293B"
                              filter="drop-shadow(0 8px 16px rgba(0,0,0,0.3))"
                            />
                            <text x="12" y="19" fontSize="11" fontWeight="700" fill="#FFFFFF">
                              {activePt.fullLabel || activePt.dayLabel}
                            </text>
                            <line x1="0" y1="26" x2={cardWidth} y2="26" stroke="#334155" strokeWidth="1" />

                            {/* Row 1: Visits */}
                            <circle cx="18" cy="42" r="4.5" fill="#2563EB" />
                            <text x="28" y="46" fontSize="11" fontWeight="500" fill="#94A3B8">
                              {t('userActivity.colTotalVisits') || 'Visits'}:
                            </text>
                            <text x={cardWidth - 12} y="46" textAnchor="end" fontSize="12" fontWeight="700" fill="#FFFFFF">
                              {activePt.val}
                            </text>

                            {/* Row 2: Unique Users */}
                            <circle cx="18" cy="59" r="4.5" fill="#0284C7" />
                            <text x="28" y="63" fontSize="11" fontWeight="500" fill="#94A3B8">
                              {t('userActivity.colUniqueVisitors') || 'Unique Users'}:
                            </text>
                            <text x={cardWidth - 12} y="63" textAnchor="end" fontSize="12" fontWeight="700" fill="#FFFFFF">
                              {activePt.uniqueVisitors}
                            </text>
                          </g>
                        </g>
                      );
                    })()}
                  </svg>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Graph 2: Top Entities Engagement Bar Chart */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChart sx={{ color: '#0284C7' }} />
                {t('userActivity.topEntitiesTitle') || 'Top'} {activeModule === 'Contact' ? (t('nav.contacts') || 'Contacts') : activeModule === 'Event' ? (t('nav.events') || 'Events') : activeModule === 'Promotion' ? (t('nav.promotions') || 'Promotions') : (t('nav.accommodation') || 'Accommodations')}
              </Typography>
            </Box>

            {topEntities.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#9CA3AF', py: 4, textAlign: 'center' }}>
                {t('userActivity.topEntitiesNoData') || `No top ${activeModule.toLowerCase()} activity data available`}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 1,
                  maxHeight: '270px',
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: '#F1F5F9',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: '#CBD5E1',
                    borderRadius: '3px',
                    '&:hover': {
                      bgcolor: '#94A3B8',
                    },
                  },
                }}
              >
                {topEntities.map((item, idx) => {
                  const maxVal = topEntities[0]?.totalVisits || 1;
                  const pct = Math.min(100, Math.max(10, Math.round(((item.totalVisits || 0) / maxVal) * 100)));
                  const colors = ['#328284', '#0284C7', '#16A34A', '#9333EA', '#F59E0B'];
                  const barColor = colors[idx % colors.length];

                  return (
                    <Box key={item.entityId || idx}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                          {item.entityName || `Entity #${item.entityId}`}
                        </Typography>
                        <Chip
                          label={`${item.totalVisits || 0} ${t('userActivity.colTotalVisits') || 'visits'}`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.72rem', fontWeight: 700, bgcolor: `${barColor}15`, color: barColor }}
                        />
                      </Box>
                      <Box sx={{ width: '100%', bgcolor: '#F3F4F6', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${pct}%`,
                            bgcolor: barColor,
                            height: '100%',
                            borderRadius: 4,
                            transition: 'width 0.6s ease-in-out',
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Filter Section Paper */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: '#111827' }}>
          {t('userActivity.filterTitle') || `Filter ${activeModule} Activity`}
        </Typography>

        <Grid container spacing={2.5} alignItems="center">
          {/* Search Filter */}
          <Grid item xs={12} sm={6} md={activeModule === 'Contact' ? 4 : 5}>
            <TextField
              fullWidth
              size="small"
              label={`${t('common.search') || 'Search'} ${activeModule} ${t('userActivity.searchPlaceholder') || 'Name or Email'}`}
              placeholder={t('userActivity.typeToFilter') || 'Type to filter...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: '#FFFFFF',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: '#D1D5DB' },
                  '&:hover fieldset': { borderColor: '#328284' },
                  '&.Mui-focused fieldset': { borderColor: '#328284' },
                },
              }}
            />
          </Grid>

          {/* Category Dropdown - Only shown for Contact Module */}
          {activeModule === 'Contact' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('userActivity.categoryLabel') || 'Category'}</InputLabel>
                <Select
                  value={selectedCategory}
                  label={t('userActivity.categoryLabel') || 'Category'}
                  onChange={handleCategorySelect}
                  sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#328284' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#328284' },
                  }}
                >
                  <MenuItem value="all">{t('userActivity.allCategories') || 'All Categories'}</MenuItem>
                  {categoryOptions.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Select Date Filter */}
          <Grid item xs={12} sm={6} md={activeModule === 'Contact' ? 3 : 4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('userActivity.selectDate') || 'Select Date'}
              value={selectedDate}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                bgcolor: '#FFFFFF',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: '#D1D5DB' },
                  '&:hover fieldset': { borderColor: '#328284' },
                  '&.Mui-focused fieldset': { borderColor: '#328284' },
                },
              }}
            />
          </Grid>

          {/* Clear Filters Button */}
          <Grid item xs={12} sm={6} md={2} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<FilterAltOff />}
              sx={{
                ...clearFilterButtonSx,
                height: 40,
                width: 'auto',
                minWidth: 'auto',
                maxWidth: '100%',
                px: 2,
                borderRadius: '10px',
                borderColor: '#0F3D3E',
                color: '#0F3D3E',
                '&:hover': {
                  borderColor: '#0A2829',
                  bgcolor: '#EBF4F4',
                },
              }}
            >
              {t('common.clearFilters') || 'Clear Filters'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Activity Data Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#328284' }} />
          </Box>
        ) : filteredActivities.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <History sx={{ fontSize: 48, color: '#9CA3AF', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#4B5563', fontWeight: 600 }}>
              {t('userActivity.noRecords') || `No ${activeModule} Activity Records Found`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
              Try adjusting your search query or clear filters to view data.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader aria-label="user activity table">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#F3F4F6', fontWeight: 700, color: '#374151' } }}>
                    <TableCell>{activeModule} {t('userActivity.colInformation') || 'Information'}</TableCell>
                    <TableCell>{t('userActivity.colCategory') || 'Category'}</TableCell>
                    <TableCell>{t('userActivity.colCity') || 'City'}</TableCell>
                    <TableCell align="center">{t('userActivity.colTotalVisits') || 'Total Visits'}</TableCell>
                    <TableCell align="center">{t('userActivity.colUniqueVisitors') || 'Unique Visitors'}</TableCell>
                    <TableCell>{t('userActivity.colLastActivity') || 'Last Activity Date'}</TableCell>
                    <TableCell align="center">{t('userActivity.colAction') || 'Action'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActivities.map((row, idx) => (
                    <TableRow
                      key={row.entityId || idx}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#F9FAFB' },
                      }}
                    >
                      {/* Entity Info */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: currentModuleConfig.color, fontWeight: 700, width: 42, height: 42 }}>
                            {(row.entityName || activeModule).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                              {row.entityName || `Entity #${row.entityId}`}
                            </Typography>
                            {row.entityEmail && (
                              <Typography variant="caption" sx={{ color: '#6B7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Email sx={{ fontSize: 13 }} /> {row.entityEmail}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {row.categoryName || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* City */}
                      <TableCell>
                        <Chip
                          label={row.cityName || 'Germany'}
                          size="small"
                          icon={<LocationOn sx={{ fontSize: '14px !important' }} />}
                          sx={{ bgcolor: '#F3F4F6', color: '#4B5563', height: 24, fontSize: '0.75rem' }}
                        />
                      </TableCell>

                      {/* Total Visits Chip */}
                      <TableCell align="center">
                        <Chip
                          label={row.totalVisits || 0}
                          icon={<TrendingUp sx={{ fontSize: '15px !important' }} />}
                          sx={{
                            bgcolor: '#E6F4F1',
                            color: '#0F3D3E',
                            fontWeight: 700,
                            px: 1,
                            borderRadius: '16px',
                          }}
                        />
                      </TableCell>

                      {/* Unique Visitors Chip */}
                      <TableCell align="center">
                        <Chip
                          label={row.uniqueVisitors || 0}
                          icon={<People sx={{ fontSize: '15px !important' }} />}
                          sx={{
                            bgcolor: '#E0F2FE',
                            color: '#0284C7',
                            fontWeight: 700,
                            px: 1,
                            borderRadius: '16px',
                          }}
                        />
                      </TableCell>

                      {/* Last Activity */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#4B5563' }}>
                          {formatDateTime(row.lastActivityAtUtc)}
                        </Typography>
                      </TableCell>

                      {/* Action Icon Button Click */}
                      <TableCell align="center">
                        <Tooltip title={t('userActivity.viewDetailsTooltip') || 'View Activity Details'}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewClick(row)}
                            sx={{
                              color: currentModuleConfig.color,
                              bgcolor: `${currentModuleConfig.color}15`,
                              '&:hover': { bgcolor: currentModuleConfig.color, color: '#fff' },
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 50]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Visitor Activity Detail Modal / Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F3D3E', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility sx={{ color: currentModuleConfig.color }} />
              {activeModule} {t('userActivity.modalTitle') || 'Visitor Activity Details'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              {t('userActivity.modalSub') || `List of users and visitors who interacted with this ${activeModule.toLowerCase()}.`}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDetails}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ my: 1 }} />

        <DialogContent>
          {isDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: currentModuleConfig.color }} />
            </Box>
          ) : selectedEntityDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Entity Banner Card */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: '#E6F4F1',
                  borderRadius: '12px',
                  borderLeft: `5px solid ${currentModuleConfig.color}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                    {selectedEntityDetail.entityName || 'Entity Details'}
                  </Typography>
                  {selectedEntityDetail.entityEmail && (
                    <Typography variant="body2" sx={{ color: '#374151', mt: 0.25 }}>
                      Email: <strong>{selectedEntityDetail.entityEmail}</strong>
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#374151', mt: 0.25 }}>
                    {t('userActivity.colCategory') || 'Category'}: <strong>{selectedEntityDetail.categoryName || 'N/A'}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.5 }}>
                    Location: {selectedEntityDetail.cityName || 'Germany'}
                  </Typography>
                </Box>

                {/* Counts Badges */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ ml: 'auto', gap: 1 }}>
                  <Chip
                    icon={<TrendingUp sx={{ color: '#fff !important', fontSize: 18 }} />}
                    label={`${t('userActivity.colTotalVisits') || 'Total Visits'}: ${selectedEntityDetail.totalVisits || 0}`}
                    sx={{ bgcolor: '#328284', color: '#fff', fontWeight: 700, py: 0.5, px: 1, fontSize: '0.85rem' }}
                  />
                  <Chip
                    icon={<People sx={{ color: '#fff !important', fontSize: 18 }} />}
                    label={`${t('userActivity.colUniqueVisitors') || 'Unique Visitors'}: ${selectedEntityDetail.uniqueVisitors || 0}`}
                    sx={{ bgcolor: '#0284C7', color: '#fff', fontWeight: 700, py: 0.5, px: 1, fontSize: '0.85rem' }}
                  />
                </Stack>
              </Box>

              {/* Visitors Detailed Table */}
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mt: 1 }}>
                {t('userActivity.modalLogTitle') || 'Visitor Activity Log'} ({selectedEntityDetail.visitorTotal || (selectedEntityDetail.visitors || []).length})
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('userActivity.colVisitor') || 'Visitor'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('userActivity.colEmail') || 'Email'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('userActivity.colCity') || 'City'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('userActivity.colVisitedDate') || 'Visited Date & Time'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!selectedEntityDetail.visitors || selectedEntityDetail.visitors.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#6B7280' }}>
                          {t('userActivity.noVisitorLogs') || `No visitor activity logs found for this ${activeModule.toLowerCase()}.`}
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedEntityDetail.visitors.map((v, idx) => {
                        const name = v.userName || (v.isGuest ? (t('userActivity.guest') || 'Guest User') : (t('userActivity.user') || 'Registered User'));
                        return (
                          <TableRow key={v.interestId || idx} hover>
                            {/* Visitor Name & Type */}
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                                {name}
                              </Typography>
                              {name.toLowerCase() !== 'guest' && name.toLowerCase() !== 'guest user' && (
                                <Chip
                                  label={v.isGuest ? (t('userActivity.guest') || 'Guest') : (t('userActivity.user') || 'User')}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    bgcolor: v.isGuest ? '#F3F4F6' : '#E0F2FE',
                                    color: v.isGuest ? '#6B7280' : '#0284C7',
                                    mt: 0.25,
                                  }}
                                />
                              )}
                            </TableCell>

                            {/* Email Column */}
                            <TableCell>
                              <Typography variant="body2" sx={{ color: v.email ? '#374151' : '#9CA3AF' }}>
                                {v.email || '-'}
                              </Typography>
                            </TableCell>

                            {/* City Column */}
                            <TableCell>
                              <Chip
                                label={v.cityName || 'Germany'}
                                size="small"
                                icon={<LocationOn sx={{ fontSize: '13px !important' }} />}
                                sx={{ bgcolor: '#F3F4F6', color: '#4B5563', height: 22, fontSize: '0.72rem' }}
                              />
                            </TableCell>

                            {/* Date & Time Column */}
                            <TableCell>
                              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
                                {formatDateTime(v.visitedAtUtc)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#6B7280', py: 4, textAlign: 'center' }}>
              No detail data found.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              bgcolor: currentModuleConfig.color,
              color: '#fff',
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              '&:hover': { opacity: 0.9 },
            }}
          >
            {t('userActivity.close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserActivity;
